import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../../state'
import * as modals from '../../../modals'
import * as shared from '../../shared'
/**
 * Component for displaying and editing functional usage in cluster details
 */
@ngCore.Component({
  imports: [
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.notice.Component,
    modals.clusterFunctionalUsage.Component,
    shared.functionalUsageFunctionalizations.Component,
  ],
  selector: 'atn-space-cluster-functional-usage',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.full.clusters.plugins.functionalUsage.Plugin} */
  private readonly _functionalUsagePlugin = ngCore
    .inject(state.space.full.clusters.Collection)
    .plugins.lookup(state.space.full.clusters.plugins.functionalUsage.Plugin)
    .get()

  /** @see {@link state.space.full.functions.Collection} */
  private readonly _spaceFunctionsEc = ngCore.inject(
    state.space.full.functions.Collection,
  )

  /**
   * @see {@link state.space.surveyConfiguration.Feature}
   */
  private readonly _surveyConfig = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )

  /** @see {@link state.space.full.warnings.plugin.Plugin} */
  public readonly clusterWarningsPlugin = ngCore
    .inject(state.space.full.clusters.Collection)
    .plugins.lookup(state.space.full.warnings.plugin.Plugin)
    .get()

  /** @see {@link ents.core.display.Style} */
  public readonly DisplayStyle = ents.core.display.Style

  /** @see {@link ents.extra.space.Warning.Code} */
  public readonly WarningCode = ents.extra.space.Warning.Code

  /** Combined loading state */
  public readonly isLoadingSg = ngCore.computed(
    (
      isCollectionLoading = this._spaceFunctionsEc.plugins.currentListPlugin.feature.selectors.isLoading.asSignal(),
      isFeatureLoading = this._functionalUsagePlugin.selectors.isLoading.asSignal(),
      isWarningsLoading = this.clusterWarningsPlugin.selectors.isLoading.asSignal(),
    ) => isCollectionLoading || isFeatureLoading || isWarningsLoading,
  )

  /** Generated chart data @see {@link ents.core.display.chart.data.Slice[]}  */
  public readonly functionalUsageChartDataSg = ngCore.computed(
    (
      functionalizations = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
      spaceFunctions = this._spaceFunctionsEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
    ) => {
      if (!functionalizations?.length) {
        return { slices: [] }
      }

      const functionCodeMap = new Map(
        spaceFunctions
          .filter(TypeChecker.isNotNullish.raw)
          .map(func => [func.id, func.code]),
      )

      return {
        slices: [...functionalizations]
          .sort((a, b) => b.percent - a.percent)
          .map(func => ({
            name: functionCodeMap.get(func.functionId) ?? func.functionId,
            value: func.percent * 100,
          })),
      }
    },
  )

  /** All allocations including ones not in current usage (with 0%) */
  public readonly allAllocationsSg = ngCore.computed<
    shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent[]
  >(
    (
      functionalizations = this._functionalUsagePlugin.selectors.functionalUsage.asSignal(),
      spaceFunctions = this._spaceFunctionsEc.plugins.currentListPlugin.feature.selectors.entities.asSignal(),
      surveyConfig = this._surveyConfig.selectors.config.asSignal(),
    ) => {
      const existingMap = new Map(
        (functionalizations ?? []).map(f => [f.functionId, f.percent]),
      )

      const getTagsForFunction = (
        func: ents.extra.space.Function,
      ): shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent['tags'] => {
        const tags: shared.functionalUsageFunctionalizations.SpaceFunctionWithPercent['tags'] =
          []

        if (
          surveyConfig?.functionalUsage
            ?.requireSponsoredAccountAssignmentsForSponsoredFunctionalUsages &&
          func.isSponsored
        ) {
          tags.push({
            text: 'Sponsored',
            style: ds.color.Legacy.PredefinedStyle.Info,
          })
        }

        if (
          surveyConfig?.functionalUsage
            ?.requireFunctionalUsageAssignmentsForSelectedAccounts &&
          func.isRequiredForMatchingPayroll
        ) {
          tags.push({
            text: 'Required',
            style: ds.color.Legacy.PredefinedStyle.Warning,
          })
        }

        return tags
      }

      // Display all non-exclusive space functions with their values or 0%
      return spaceFunctions
        .filter(TypeChecker.isNotNullish.raw)
        .filter(func => !func.isExclusive)
        .map(func => ({
          ...func,
          percent: existingMap.get(func.id) ?? 0,
          tags: getTagsForFunction(func),
        }))
    },
  )

  /** Handle edit button click */
  public editClickHandler(): void {
    this._functionalUsagePlugin.actions.beginUpdating
      .from('Space.ClusterDetails.FunctionalUsage.Edit.Clicked')
      .dispatch()
  }
}
